import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ClinicalStepper } from "@/components/common/ClinicalStepper";
import { Avatar } from "@/components/common/Avatar";
import { initials } from "@/lib/format";
import { STAGES, STAGE_LABELS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { FolderKanban, Plus, Search, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function Cases() {
  const navigate = useNavigate();
  const [cases, setCases] = useState(null);
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", title: "", treatment: "Implant Placement" });

  const load = () => api.get("/cases").then((r) => setCases(r.data)).catch(() => setCases([]));
  useEffect(() => { load(); api.get("/patients").then((r) => setPatients(r.data)); }, []);

  const create = async () => {
    if (!form.patient_id) return toast.error("Select a patient");
    try {
      const { data } = await api.post("/cases", form);
      toast.success("Case created");
      setOpen(false);
      navigate(`/cases/${data.id}`);
    } catch { toast.error("Could not create case"); }
  };

  const filtered = (cases || []).filter((c) => {
    const matchQ = c.title.toLowerCase().includes(q.toLowerCase()) || (c.patient_name || "").toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "all" || c.stage === filter;
    return matchQ && matchF;
  });

  const AddDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-case-btn" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg"><Plus className="h-4 w-4 mr-1.5" />New Case</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Create New Case</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs font-semibold">Patient</Label>
            <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
              <SelectTrigger className="mt-1" data-testid="case-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
              <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.patient_id}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs font-semibold">Case title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Single Implant — Tooth 36" className="mt-1" /></div>
          <div><Label className="text-xs font-semibold">Treatment</Label><Input value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })} className="mt-1" /></div>
        </div>
        <DialogFooter><Button onClick={create} data-testid="save-case-btn" className="w-full bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">Create Case</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div>
      <PageHeader title="Cases" subtitle="Track every patient through the clinical journey" breadcrumb={["Workspace", "Cases"]} actions={AddDialog} />
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B9698]" />
          <Input data-testid="case-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search cases..." className="pl-9 h-10" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px] h-10" data-testid="case-stage-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {STAGES.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {cases && filtered.length === 0 ? (
        <EmptyState icon={FolderKanban} title="No cases found" description="Create a case to begin managing a patient's clinical workflow." actionLabel="New Case" onAction={() => setOpen(true)} testId="cases-empty" />
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => (
            <SurfaceCard key={c.id} className="p-5 cursor-pointer hover:border-[#00A85A]/40" data-testid={`case-card-${c.id}`} onClick={() => navigate(`/cases/${c.id}`)}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex items-center gap-3 lg:w-64 shrink-0">
                  <Avatar name={c.patient_name} size={40} />
                  <div className="min-w-0"><div className="text-sm font-bold text-[#101516] dark:text-white truncate">{c.title}</div><div className="text-xs text-[#8B9698]">{c.case_id} · {c.patient_name}</div></div>
                </div>
                <div className="flex-1 min-w-0"><ClinicalStepper current={c.stage} done={c.stages_done || []} /></div>
                <div className="flex items-center gap-4 shrink-0">
                  {c.priority === "high" && <StatusBadge status="error" label="High Priority" />}
                  <div className="text-right"><div className="text-xs font-semibold text-[#00A85A]">{c.readiness}% ready</div></div>
                  <ChevronRight className="h-4 w-4 text-[#8B9698]" />
                </div>
              </div>
            </SurfaceCard>
          ))}
        </div>
      )}
    </div>
  );
}
